import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient, fleetPath } from '@/lib/api-client';
import type { EventType, FleetEvent } from '@/types/fleet';

/** A fleet-wide event row — a `FleetEvent` tagged with its server. */
export interface FleetFeedEvent extends FleetEvent {
  server_name: string;
}

/** Live API shape (camelCase) for one feed row. */
interface ApiFleetFeedEvent {
  id: number;
  eventType: string;
  source: string;
  payload: Record<string, unknown>;
  occurredAt: string;
  serverName: string | null;
}

/**
 * GET /api/admin/fleet/events — the fleet-wide recent-event feed that drives
 * the dashboard radiator. Filterable by event type; refreshed every 30s.
 * The API serves camelCase; rows are adapted to the snake_case contract.
 */
export function useFleetEventFeed(
  eventTypes: EventType[],
  limit = 50,
): UseQueryResult<FleetFeedEvent[], Error> {
  return useQuery({
    queryKey: ['fleet', 'event-feed', eventTypes, limit] as const,
    queryFn: ({ signal }) =>
      apiClient
        .get<{ items: ApiFleetFeedEvent[] }>(
          fleetPath('/events', { event_type: eventTypes, limit }),
          signal,
        )
        .then((r) =>
          r.items.map(
            (e): FleetFeedEvent => ({
              id: e.id,
              event_type: e.eventType as FleetEvent['event_type'],
              source: e.source,
              payload: e.payload,
              occurred_at: e.occurredAt,
              server_name: e.serverName ?? '',
            }),
          ),
        ),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}
